/**
 * Typed, validated access to build-time environment.
 *
 * Only `VITE_`-prefixed vars reach the browser bundle, and every one of them is
 * public — never put a secret here. Read env through this module, never
 * `import.meta.env` directly, so a missing var fails loudly at startup instead
 * of surfacing as `undefined` deep in a request.
 */

type RawEnv = ImportMetaEnv;

function required(raw: RawEnv, key: keyof ImportMetaEnv & string): string {
    const value = raw[key];
    if (typeof value !== 'string' || value.length === 0) {
        throw new Error(
            `Missing required environment variable "${key}". Add it to your .env file (see .env.example).`
        );
    }
    return value;
}

function optional(
    raw: RawEnv,
    key: keyof ImportMetaEnv & string,
    fallback: string
): string {
    const value = raw[key];
    return typeof value === 'string' && value.length > 0 ? value : fallback;
}

// A regex-free trailing-slash trim — a regex equivalent flags as
// super-linear-backtracking-prone under static analysis even though `+$` is
// anchored and safe here; a plain loop sidesteps the question entirely.
function stripTrailingSlashes(value: string): string {
    let end = value.length;
    while (end > 0 && value[end - 1] === '/') {
        end -= 1;
    }
    return value.slice(0, end);
}

export function readEnv(raw: RawEnv = import.meta.env) {
    // One origin for the main API. The backend serves both halves from a
    // single API Gateway: Apollo Server at POST/GET /graphql, and the Express
    // app (auth, OAuth, /me, /spots, /subscription) on the ANY /{proxy+}
    // catch-all underneath it. Deriving both URLs from one base is what keeps
    // them from drifting.
    //
    // The web consent shell (ticket 726) talks to a *second* origin — the
    // `connect.` host serving `/oauth/consent/*` — so the CSP now needs two
    // `connect-src` entries, not one. See `connectBaseUrl` below.
    const apiBaseUrl = stripTrailingSlashes(required(raw, 'VITE_API_BASE_URL'));
    const connectBaseUrl = stripTrailingSlashes(
        required(raw, 'VITE_CONNECT_BASE_URL')
    );

    return {
        apiBaseUrl,
        graphqlEndpoint: `${apiBaseUrl}/graphql`,
        // The `connect.` origin serving the OAuth consent endpoints. Required
        // so a missing value fails loudly at startup rather than surfacing as
        // `undefined` in a request.
        connectBaseUrl,
        // The web app's public OAuth client id. Not a secret — public clients
        // have no client_secret by design (D1).
        oauthClientId: required(raw, 'VITE_OAUTH_CLIENT_ID'),
        // Google's web client id. Optional: an empty value is a legitimate
        // configuration meaning "no Google here", not a startup failure.
        googleClientId: optional(raw, 'VITE_GOOGLE_CLIENT_ID', ''),
        appName: optional(raw, 'VITE_APP_NAME', 'Dawn Patrol Surf Tracking'),
        isProduction: raw.PROD,
        isDevelopment: raw.DEV,
    } as const;
}

export type Env = ReturnType<typeof readEnv>;

export const env: Env = readEnv();
